import React, { useState, useEffect } from 'react';
import { Download, X, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const InstallAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isStandalone) {
      setShowBanner(false);
      return;
    }

    // Always show on mobile devices (don't check dismissed for now)
    const isMobile = isIOS || isAndroid || window.innerWidth < 768;
    if (isMobile) {
      // Small delay to ensure page is loaded
      setTimeout(() => setShowBanner(true), 500);
    }

    // Listen for Android install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isIOS, isAndroid, isStandalone]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android - trigger native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // Show instructions (iOS or Android without native prompt)
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', new Date().toISOString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-xl shadow-lg mb-6 relative">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/70 hover:text-white"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="text-white" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Install Mipay App</h3>
            <p className="text-blue-100 text-sm">Get quick access & push notifications</p>
          </div>
          <Button 
            onClick={handleInstallClick}
            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
          >
            <Download size={18} className="mr-2" />
            Install
          </Button>
        </div>
      </div>

      {/* Instructions Modal - iOS and Android */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Install Mipay App</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              {isIOS ? (
                <>
                  <p className="text-slate-600 mb-6">Follow these steps to install on your <strong>iPhone/iPad</strong>:</p>
                  
                  <div className="space-y-6">
                    {/* iOS Step 1 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Open in Safari</p>
                        <p className="text-slate-500 text-sm">This only works in Safari browser</p>
                      </div>
                    </div>
                    
                    {/* iOS Step 2 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap the Share button</p>
                        <p className="text-slate-500 text-sm">At the bottom of Safari:</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Share size={24} className="text-blue-600" />
                          <span className="text-sm text-slate-600">Share</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* iOS Step 3 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Add to Home Screen"</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Plus size={20} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Add to Home Screen</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* iOS Step 4 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Add"</p>
                        <p className="text-slate-500 text-sm">Mipay will appear on your home screen!</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-600 mb-6">Follow these steps to install on your <strong>Android phone</strong>:</p>
                  
                  <div className="space-y-6">
                    {/* Android Step 1 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Open in Chrome</p>
                        <p className="text-slate-500 text-sm">Use Google Chrome browser</p>
                      </div>
                    </div>
                    
                    {/* Android Step 2 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap the Menu (⋮)</p>
                        <p className="text-slate-500 text-sm">Three dots at top right corner</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <span className="text-xl text-slate-600">⋮</span>
                          <span className="text-sm text-slate-600">Menu</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Android Step 3 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Install app" or "Add to Home screen"</p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                          <Download size={20} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Install app</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Android Step 4 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Tap "Install"</p>
                        <p className="text-slate-500 text-sm">Mipay will appear on your home screen!</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              <Button 
                onClick={() => setShowInstructions(false)}
                className="w-full mt-6"
              >
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppBanner;
